import React, { useState } from 'react';
import { Check, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AnimatedSection } from '../components/AnimatedSection';
import { memberships } from '../mock';

export const Memberships = () => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  const whatsappNumber = '51939573726';
  const whatsappContactLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hola, estoy interesado')}`;
  const whatsappPaymentLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Hola, he realizado el pago y quiero enviar el comprobante')}`;
  const yapeNumber = '+51 933 545 176';

  return (
    <div className="min-h-screen bg-black pt-24 pb-20 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <AnimatedSection animation="fade-zoom" className="text-center mb-16">
          <span className="px-4 py-2 bg-gradient-to-r from-blue-600/20 to-sky-500/20 border border-sky-500/30 rounded-full text-sky-400 text-sm font-semibold backdrop-blur-sm inline-block mb-6 animate-bounce" style={{ animationDuration: '2s' }}>
            PLANES Y PRECIOS
          </span>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
              Elige Tu
            </span>
            <br />
            <span className="bg-gradient-to-r from-blue-400 to-sky-400 bg-clip-text text-transparent">
              Membresía Perfecta
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Planes flexibles diseñados para adaptarse a tus objetivos y estilo de vida
          </p>
        </AnimatedSection>

        {/* Membership Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
          {memberships.map((membership, index) => (
            <AnimatedSection 
              key={membership.id}
              animation="slide-up-bounce"
              delay={index * 0.15}
            >
              <Card
                className={`relative bg-gradient-to-br from-gray-900 to-black border transition-all duration-500 hover:scale-110 hover:shadow-2xl ${
                  membership.popular
                    ? 'border-sky-500 shadow-2xl shadow-sky-500/30 ring-2 ring-sky-500/50 animate-glow-pulse'
                    : 'border-sky-500/20 hover:border-sky-500/50'
                }`}
              >
              {membership.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-blue-600 to-sky-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg flex items-center space-x-1">
                    <Sparkles className="h-4 w-4" />
                    <span>MÁS POPULAR</span>
                  </span>
                </div>
              )}

              <CardHeader className="text-center pb-8 pt-8">
                <CardTitle className="text-2xl font-bold text-white mb-2">
                  {membership.name}
                </CardTitle>
                <CardDescription className="text-gray-400">
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-white">S/{membership.price}</span>
                    <span className="text-gray-400 ml-2 text-sm">/ {membership.period}</span>
                  </div>
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                <ul className="space-y-4">
                  {membership.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <div className="bg-gradient-to-br from-blue-600 to-sky-500 p-1 rounded-full flex-shrink-0 mt-0.5">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full font-semibold py-6 transition-all duration-300 ${
                    membership.popular
                      ? 'bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50'
                      : 'bg-white/5 hover:bg-white/10 text-white border border-sky-500/30 hover:border-sky-500'
                  }`}
                >
                  Seleccionar Plan
                </Button>
              </CardContent>
            </Card>
            </AnimatedSection>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <Card className="bg-gradient-to-br from-gray-900 to-black border-sky-500/20 max-w-4xl mx-auto">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Beneficios Adicionales</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left mb-8">
                <div className="flex items-center space-x-3">
                  <Check className="h-5 w-5 text-sky-400 flex-shrink-0" />
                  <span className="text-gray-300">Sin permanencia mínima</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Check className="h-5 w-5 text-sky-400 flex-shrink-0" />
                  <span className="text-gray-300">Cancelación flexible</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Check className="h-5 w-5 text-sky-400 flex-shrink-0" />
                  <span className="text-gray-300">Entrenamiento personalizado incluido</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Check className="h-5 w-5 text-sky-400 flex-shrink-0" />
                  <span className="text-gray-300">Asesoría con nutrióloga</span>
                </div>
              </div>

              {/* Promociones especiales */}
              <div className="border-t border-sky-500/20 pt-6 mt-6">
                <h4 className="text-xl font-bold text-sky-400 mb-4">Promociones Especiales</h4>
                <div className="space-y-4">
                  <div className="bg-blue-600/10 border border-sky-500/30 rounded-lg p-4">
                    <h5 className="font-semibold text-white mb-2"> Día de Prueba Gratuito</h5>
                    <p className="text-gray-300 text-sm">
                      Agenda una sesión inicial sin costo escribiendo directamente a nuestro WhatsApp para conocer las instalaciones.
                    </p>
                  </div>
                  <div className="bg-blue-600/10 border border-sky-500/30 rounded-lg p-4">
                    <h5 className="font-semibold text-white mb-2"> Promoción en Pareja</h5>
                    <p className="text-gray-300 text-sm">
                      Paquete dúo de 2 personas por <span className="text-sky-400 font-bold">S/ 299</span> - ¡Entrena con tu compañero y ahorra!
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold px-8 py-4 text-lg shadow-lg transition-all duration-300 hover:scale-105"
                  onClick={() => window.open(whatsappContactLink, '_blank')}
                >
                   Contactar por WhatsApp
                </Button>
                
                {/* Payment Modal */}
                <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
                  <DialogTrigger asChild>
                    <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold px-8 py-4 text-lg shadow-lg transition-all duration-300 hover:scale-105">
                       Pagar con Yape/Plin
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-gradient-to-br from-gray-900 to-black border-sky-500/30 text-white max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-bold text-center mb-4">
                        Pagar con Yape/Plin
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6">
                      <div className="text-center">
                        <p className="text-gray-300 mb-4">Realiza tu pago al siguiente número:</p>
                        
                        {/* QR Code Placeholder */}
                        <div className="bg-white p-6 rounded-xl mx-auto w-fit mb-4">
                          <div className="w-48 h-48 bg-gradient-to-br from-purple-200 to-pink-200 rounded-lg flex items-center justify-center">
                            <div className="text-center">
                              <p className="text-purple-800 font-bold text-lg mb-2">Yape/Plin</p>
                              <p className="text-purple-900 font-bold text-2xl">{yapeNumber}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-4 mb-4">
                          <p className="text-sky-400 font-semibold text-lg">{yapeNumber}</p>
                          <p className="text-gray-400 text-sm mt-1">Número para Yape/Plin</p>
                        </div>
                      </div>
                      
                      <Button 
                        className="w-full bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold py-6 text-lg transition-all duration-300 hover:scale-105"
                        onClick={() => {
                          window.open(whatsappPaymentLink, '_blank');
                          setShowPaymentModal(false);
                        }}
                      >
                         Enviar Comprobante por WhatsApp
                      </Button>
                      
                      <p className="text-gray-400 text-sm text-center">
                        Después de realizar el pago, envíanos tu comprobante por WhatsApp para confirmar tu membresía.
                      </p>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
